import { CSSProperties } from 'react';
import { getCdnUrl } from 'base/utils/trans-path';
import { IFeatureBase, IFeature, IFeatureTheme, IFeaturesConfig } from 'mod/featureV2/interface';
import { IPageConfig } from 'mod/brochure/interface';
import { isDark } from 'base/utils/color';
import { transJsonKeys } from 'ava-util/dist/data';
import { isPhone } from 'base/utils/client';
import { IBanner, IFeatureBanner, IOldBanner } from 'mod/featureV2/interface/banner';
import { clone } from 'ava-util/dist/data';

export function getBackgroundStyle(background: IFeatureBase['background']): CSSProperties {
    const arr = [] as string[];
    // background 靠前的值在上层
    if (background?.gradientTop) {
        arr.push(background.gradientTop);
    }
    if (background?.image) {
        const {
            positionX = 'center',
            positionY = 'top',
            repeat = 'no-repeat',
            size = '100% auto',
        } = background;
        arr.push(`url('${getCdnUrl(background.image)}') ${repeat} ${positionX} ${positionY === 'middle' ? 'center' : positionY} / ${size}`);
    }
    if (background?.gradientBottom) {
        arr.push(background.gradientBottom);
    }
    if (background?.color) {
        arr.push(background.color);
    }
    return arr.length > 0 ? {
        background: arr.join(',')
    } : {};
}

export function getSpaceStyle(space: IFeatureBase['space']): CSSProperties {
    const style: CSSProperties = {};
    if (space) {
        Object.entries(space).forEach(([key, value]) => {
            if (typeof value === 'number') {
                style[key] = value === 0 ? 0 : `${value}px`;
            }
        });
    }
    return style;
}

export function getWrapStyle(theme?: IFeatureTheme): CSSProperties {
    if (!theme) {
        return {};
    }
    const { background, space, vars } = theme;
    return {
        ...getBackgroundStyle(background),
        ...getSpaceStyle(space),
        ...vars
    };
}

export function getContainerThemeProps(theme?: IFeatureTheme): { className?: string; style?: CSSProperties } {
    const props: { className?: string; style?: CSSProperties } = {
        className: 'g-features-default'
    }
    if (theme) {
        const bgColor = theme.background?.color;
        if (bgColor) {
            props.className = isDark(bgColor) ? 'g-features-dark' : 'g-features-light';
        }
        props.style = getWrapStyle(theme);
    }
    return props;
}

// container id
export function getModuleElementId(moduleId?: string): string {
    return moduleId ? moduleId.trim().replaceAll(' ', '-').toLowerCase() : ''
}

// 获取打点module code
export function getModuleCode(moduleId?: string): string {
    return moduleId ? `${process.env.NEXT_PUBLIC_TERMINAL === 'm' ? 'H5' : 'WEB'} ${moduleId}` : '';
}

// 根据背景色设置对应的默认文字颜色
export function getTextColorClass(bgColor: any): string {
    return isDark(bgColor) ? 'text-white' : 'text-dark';
}

export function transOldBanner(banner: IOldBanner): IBanner {
    if (banner.video) {
        const { video, ...rest } = banner;

        return transJsonKeys({ ...rest, ...video }, {
            bgColor: 'bgColor',
            videoUrl: 'src',
            videoBackUpUrl: 'backup',
            picUrl: 'poster',
            linkUrl: 'href',
        }, ['target']) as IBanner;

    } else if ('videoUrl' in banner && !!banner.videoUrl) {
        return {
            ...transJsonKeys(banner, {
                bgColor: 'bgColor',
                videoUrl: 'src',
                videoBackUpUrl: 'backup',
                picUrl: 'poster',
                target: 'target',
            }, ['target']),
            control: {
                size: isPhone ? 40 : 80,
            },
            controls: true
        } as IBanner;
    } else {
        return transJsonKeys(banner, {
            bgColor: 'bgColor',
            picUrl: 'src',
            largePicUrl: 'large',
            linkUrl: 'href',
            altText: 'alt',
        }, ['target']) as IBanner;
    }
}

export function transFeatures(features: IFeature[]): IFeature[] {
    // @ts-ignore
    return features.map(feature => {
        // @ts-ignore
        if (feature.type === 'tiledBanner') {
            return {
                // @ts-ignore
                ...feature,
                type: 'banner',
                // @ts-ignore
                list: feature.list.map(banner => {
                    if (banner.video) {
                        const { video, ...rest } = banner;

                        return transJsonKeys({ ...rest, ...video }, {
                            bgColor: 'bgColor',
                            videoUrl: 'src',
                            videoBackUpUrl: 'backup',
                            picUrl: 'poster',
                            linkUrl: 'href',
                        })

                    } else if ('videoUrl' in banner && !!banner.videoUrl) {
                        return {
                            ...transJsonKeys(banner, {
                                bgColor: 'bgColor',
                                videoUrl: 'src',
                                videoBackUpUrl: 'backup',
                                picUrl: 'poster',

                            }),
                            control: {
                                size: isPhone ? 40 : 80,
                            },
                            controls: true
                        };
                    } else {
                        return transJsonKeys(banner, {
                            bgColor: 'bgColor',
                            picUrl: 'src',
                            largePicUrl: 'large',
                            linkUrl: 'href',
                            altText: 'alt',
                        }, [])
                    }
                })
            }
        }
        return feature;
    });
}

export function transPageConfig(pageConfig: IPageConfig): IPageConfig {
    return {
        ...pageConfig,
        features: transFeatures(pageConfig.features)
    };
}

export function mergeFeatureTheme<T extends IFeatureBase>(features: T[], featureTheme?: IFeatureTheme): T[] {
    if (!featureTheme) {
        return features;
    }
    return features.map(feature => Object.assign(clone(featureTheme), feature))
}

export function mergeFeaturesConfig<T extends (IFeatureBase & { type: string })>(props: IFeaturesConfig<T>): Omit<IFeaturesConfig<T>, 'featureTheme'> {
    const { features: propsFeatures, featureTheme, containerTheme, bannerPreload } = props;
    let features = mergeFeatureTheme(propsFeatures, featureTheme);
    if (bannerPreload) {
        features = extendBannerPreload(features);
    }
    return {
        containerTheme,
        features
    }
}

export function fillFeaturesModuleId<T extends (IFeatureBase & { type: string })>(features: T[]): T[] {
    const typeMap: Record<string, number> = {};
    return features.map(feature => {
        const type = feature.type;
        if (!typeMap[type]) {
            typeMap[type] = 1;
        } else {
            typeMap[type]++;
        }
        const moduleId = `${type}-${typeMap[type]}`;
        return {
            ...feature,
            moduleId,
        };
    });
}

export function extendBannerPreload<T extends IFeatureBase & { type: string }>(features: T[]): T[] {
    let counter = 0;
    return features.map(feature => {
        if (/banner/i.test(feature.type) && counter === 0) {
            counter++;
            const bannerFeature = feature as unknown as IFeatureBanner;
            const config = bannerFeature.config || {};
            return {
                ...feature,
                config: {
                    ...config,
                    preload: true,
                }
            }
        }
        return feature;
    })
}